import React, { cloneElement } from 'react';
import { Lock } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';
import { showUpgradeRequired } from './UpgradeRequiredDialog';
import './LockedFeature.css';

// Wraps a single control (expects one element child, typically a button)
// that should be disabled and lock-iconed when the school's plan doesn't
// include `module`, instead of locking the whole page — for routers gated
// per-endpoint (see services/plan_gating.py) rather than whole-router.
// Mirrors Layout.js's nav-item--locked opacity/lock-icon pattern so a
// "premium feature" reads the same whether it's a nav item or a button.
//
// Overrides the CHILD's own onClick via cloneElement rather than adding a
// click handler on a wrapping element — a handler on a wrapper fires
// AFTER the child's own onClick has already run (DOM events reach their
// target before bubbling to ancestors), so a wrapper alone can decorate a
// locked button but can't actually stop it from doing its thing.
//
// Deliberately forces the native `disabled` attribute OFF (not on) when
// locked: a disabled element never dispatches click events at all
// (browsers suppress them entirely), which would silently swallow our own
// onClick override too — the user would click and get no feedback
// whatsoever, not even the upgrade dialog. This matters even for buttons
// that don't set `disabled` themselves, since several wrapped buttons in
// this codebase have their own data-dependent `disabled={...}` (e.g. "no
// QR token loaded yet") that could otherwise coincidentally end up true
// while locked too. `aria-disabled` conveys the same "this is inactive"
// semantics to assistive tech without blocking the click that explains why.
function LockedFeature({ module, children }) {
  const { hasModule } = useAuth();
  if (hasModule(module)) {
    return children;
  }
  const handleLockedClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    showUpgradeRequired();
  };
  return (
    <span className="locked-feature">
      {cloneElement(children, { onClick: handleLockedClick, disabled: false, 'aria-disabled': true })}
      <Lock size={12} weight="fill" className="locked-feature-icon" />
    </span>
  );
}

export default LockedFeature;
